import {
    Box,
    Typography,
    Grid,
    Button,
    Divider,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    InputBase,
    Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CheckIcon from "@mui/icons-material/Check";
import React, { useState } from "react";
import UploadedThreeFilesDisplay from "./UploadedThreeFilesDisplay";
import metadata_demo from "../../assets/examples/metadata_demo.csv";
import { validateMetadataFileStrictDetailed as validateMetadata } from "../General/validateMetadataFileFormat";
import { uploadFileTus, uploadMultipleFilesTus } from "../hooks/UseTusUpload";
import ProcessingModal from "./Modales/ProcessingModal";
import UploadCompleteModal from "./Modales/UploadCompleteModal";
import ErrorModal from "./Modales/ErrorModal";
import { apiRequest } from "../api/apiClient";

export default function AssembledGenomeCard({
    onUploadMetadata,
    onUploadR1,
    onUploadR2,
    onAnalyze,
    genomes = [
        "Acinetobacter baumannii",
        "Klebsiella pneumoniae",
        "Staphylococcus aureus",
    ],
}) {
    const [selectedFile, setSelectedFile] = useState(null);
    const [metadataFile, setMetadataFile] = useState(null);
    const [metadataError, setMetadataError] = useState(null);
    const [fastaError, setFastaError] = useState(null);
    const [formErrors, setFormErrors] = useState([]);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const [acceptedTos, setAcceptedTos] = useState(false);
    const [email, setEmail] = useState("");
    const [selectedGenome, setSelectedGenome] = useState("");

    const [openLoadModal, setOpenLoadModal] = useState(false);
    const [openUploadCompleteModal, setOpenUploadCompleteModal] = useState(false);
    const [openErrorModal, setOpenErrorModal] = useState(false);
    const [errorTitle, setErrorTitle] = useState("Connection issues");
    const [errorMessage, setErrorMessage] = useState(
        "Please check your internet connection and try uploading your files again."
    );

    const [uploadProgress, setUploadProgress] = useState([]);

    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const genomeHasError = submitAttempted && !selectedGenome;
    const metadataHasError = submitAttempted && (!metadataFile || Boolean(metadataError));
    const fastaHasError = submitAttempted && (!selectedFile || Boolean(fastaError));
    const emailHasError = submitAttempted && !isValidEmail(email);
    const tosHasError = submitAttempted && !acceptedTos;

    const showGreenCheck = (condition) => {
        return condition ? (
            <Box
                sx={{
                    width: "55px",
                    height: "55px",
                    borderRadius: "50%",
                    border: "1px solid #20BC71",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ml: 2,
                }}
            >
                <CheckIcon sx={{ color: "#20BC71", fontSize: "30px" }} />
            </Box>
        ) : null;
    };

    const removeError = (text) => {
        setFormErrors((prev) => prev.filter((err) => err !== text));
    };

    const initUploadProgress = () => {
        const files = [selectedFile, metadataFile].filter(Boolean);

        const initial = files.map((file) => ({
            fileName: file.name,
            percentage: 0,
            bytesUploaded: 0,
            bytesTotal: file.size || 0,
        }));

        setUploadProgress(initial);
    };

    const updateUploadProgress = ({ fileName, percentage, bytesUploaded, bytesTotal }) => {
        setUploadProgress((prev) => {
            const exists = prev.some((item) => item.fileName === fileName);

            if (!exists) {
                return [
                    ...prev,
                    { fileName, percentage, bytesUploaded, bytesTotal },
                ];
            }

            return prev.map((item) =>
                item.fileName === fileName
                    ? { ...item, percentage, bytesUploaded, bytesTotal }
                    : item
            );
        });
    };

    const resetForm = () => {
        setSelectedFile(null);
        setMetadataFile(null);
        setMetadataError(null);
        setFastaError(null);
        setFormErrors([]);
        setSubmitAttempted(false);
        setAcceptedTos(false);
        setEmail("");
        setSelectedGenome("");
        setUploadProgress([]);
    };

    const handleUploadMetadata = async (event) => {
        const input = event.target;

        if (!input.files || input.files.length === 0) {
            return;
        }

        const file = input.files[0];
        const lower = file.name.toLowerCase();
        const allowed = [".txt", ".tsv", ".csv", ".xls", ".xlsx"];
        const isAllowed = allowed.some((ext) => lower.endsWith(ext));

        if (!isAllowed) {
            setMetadataFile(null);
            setMetadataError("Only .txt, .tsv, .csv, .xls, .xlsx files are accepted.");
            input.value = "";
            return;
        }

        const validation = await validateMetadata(file);

        if (validation.ok) {
            setMetadataError(null);
            setMetadataFile(file);
            removeError("Upload metadata file");
            removeError("Metadata file is incomplete or incorrect");
            onUploadMetadata?.(file);
        } else {
            setMetadataFile(null);
            setMetadataError(validation.errors);
        }

        input.value = "";
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const fileName = file.name;
        const pattern = /^.+\.(fasta|fa|fna)$/i;
        const isValid = pattern.test(fileName);

        if (!isValid) {
            setSelectedFile(null);
            setFastaError(
                "Invalid filename. Accepted formats:\n" +
                "sampleID.fasta\n" +
                "sampleID.fa\n" +
                "sampleID.fna"
            );
        } else {
            setFastaError(null);
            setSelectedFile(file);
            removeError("Upload FASTA file");
            removeError("FASTA filename is invalid");
            onUploadR1?.(file);
        }

        e.target.value = "";
    };

    const validateForm = async () => {
        const errors = [];

        if (!selectedGenome) errors.push("Select a reference genome");

        if (!metadataFile) errors.push("Upload metadata file");
        if (metadataError) errors.push("Metadata file is incomplete or incorrect");

        if (!selectedFile) errors.push("Upload FASTA file");
        if (fastaError) errors.push("FASTA filename is invalid");

        if (!acceptedTos) errors.push("Accept Terms of Service");
        if (!isValidEmail(email)) errors.push("Enter a valid email");

        return { ok: errors.length === 0, errors };
    };

    async function uploadAllFiles() {
        try {
            const fileNames = await uploadMultipleFilesTus({
                files: [selectedFile],
                token: "CAMBIA_ESTE_TOKEN",
                email: email,
                bacterium: selectedGenome,
                analyze: "Assembled",
                onProgress: updateUploadProgress,
            });

            return fileNames;
        } catch (error) {
            console.error("Upload error:", error);
            throw new Error("connection_error");
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitAttempted(true);

        const { ok, errors } = await validateForm();
        setFormErrors(errors);

        if (!ok) return;

        initUploadProgress();
        setOpenUploadCompleteModal(false);
        setOpenErrorModal(false);
        setOpenLoadModal(true);

        try {
            const fileNames = await uploadAllFiles();

            const fileMetadataID = await uploadFileTus({
                file: metadataFile,
                token: "CAMBIA_ESTE_TOKEN",
                email: email,
                bacterium: selectedGenome,
                analyze: "fasta",
                onProgress: updateUploadProgress,
            });

            const payload = {
                metadataFile: fileMetadataID.tus_id,
                email: email,
                reference: selectedGenome,
                analyze: "fasta",
                layout: "na",
                filename_equiv: fileNames,
            };

            const metadataPost = await apiRequest("submit_run.php", {
                method: "POST",
                body: payload,
            });

            setOpenLoadModal(false);

            onAnalyze?.({
                metadataFile: fileMetadataID.tus_id,
                fastaFile: selectedFile,
                email,
                bacterium: selectedGenome,
                filenames: fileNames,
                response: metadataPost,
            });

            resetForm();
            setOpenUploadCompleteModal(true);

            return metadataPost;
        } catch (err) {
            console.error(err);

            setOpenLoadModal(false);
            setOpenUploadCompleteModal(false);

            const isConnectionError =
                err?.message === "connection_error" ||
                err?.code === "connection_error";

            if (isConnectionError) {
                setErrorTitle("Connection issues");
                setErrorMessage(
                    "Please check your internet connection and try uploading your files again."
                );
            } else {
                setErrorTitle("Unexpected error");
                setErrorMessage("Something went wrong. Please try again.");
            }

            resetForm();
            setOpenErrorModal(true);
        }
    };

    return (
        <Box
            sx={{
                bgcolor: "#000",
                color: "#fff",
                borderRadius: "36px",
                border: "1px solid rgba(255,255,255,0.18)",
                p: { xs: 3, md: 5 },
                width: "100%",
                maxWidth: "1920px",
                mx: "auto",
                minWidth: "400px",
            }}
        >
            <Typography sx={{ mb: 3 }} variant="h4">
                Hosts and isolation source
            </Typography>

            <Grid container sx={{ width: "100%" }} spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <Box sx={{ width: "100%", minHeight: { xs: "auto", md: "200px" } }}>
                        <Typography variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                            Each row should contain the following fields:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 3, opacity: 0.9 }}>
                            <Typography variant="body1">
                                <li>The sampleID (the identifier for each sample)</li>
                                <li>The country of origin</li>
                                <li>The host</li>
                                <li>The isolation source</li>
                                <li>The collection date (format: dd/mm/yyyy)</li>
                                <li>Authors</li>
                                <li style={{ textDecoration: "none", color: "#20BC71" }}>
                                    <a
                                        href={metadata_demo}
                                        download="Metadata_Template.csv"
                                        style={{ textDecoration: "none", color: "#20BC71" }}
                                    >
                                        Download metadata template
                                    </a>
                                </li>
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <Box
                        sx={{
                            width: "100%",
                            minHeight: { xs: "auto", md: "200px" },
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                        }}
                    >
                        <Grid>
                            <Typography sx={{ opacity: 0.9, mb: 2 }} variant="body1">
                                Each field must not contain spaces.
                            </Typography>
                            <Typography sx={{ opacity: 0.9, mb: 2 }} variant="body1">
                                The accepted file formats are: txt, tsv, csv, xls, xlsx
                            </Typography>
                        </Grid>

                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Button
                                    variant="contained"
                                    component="label"
                                    sx={{
                                        border: metadataHasError
                                            ? "1px solid #ff4d4f"
                                            : "1px solid transparent",
                                        color: "#000",
                                        borderRadius: 9999,
                                        px: 3,
                                        py: 1.25,
                                        width: "100%",
                                        maxWidth: "415px",
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        "&:hover": {
                                            bgcolor: "#111",
                                            boxShadow: "none",
                                            color: "#fff",
                                            border: "1px solid #fff",
                                        },
                                    }}
                                    color="iconoBlanco"
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        Upload your metadata file
                                    </Typography>

                                    <FileUploadIcon sx={{ width: "30px", height: "30px" }} />

                                    <input
                                        type="file"
                                        hidden
                                        accept=".txt,.tsv,.csv,.xls,.xlsx"
                                        onChange={handleUploadMetadata}
                                    />
                                </Button>

                                {metadataFile && !metadataError && showGreenCheck(true)}
                            </Box>

                            <Collapse in={Boolean(metadataError)}>
                                {metadataError && (
                                    <Box
                                        sx={{
                                            mt: 2,
                                            p: 2,
                                            border: "1px solid #ff4d4f",
                                            borderRadius: 2,
                                            bgcolor: "rgba(255, 77, 79, 0.1)",
                                        }}
                                    >
                                        <Typography
                                            sx={{ color: "#ff4d4f", fontWeight: "bold", mb: 1 }}
                                            variant="body2"
                                        >
                                            The metadata file is incomplete or incorrect:
                                        </Typography>
                                        <ul
                                            style={{
                                                margin: 0,
                                                paddingLeft: "20px",
                                                color: "#ff4d4f",
                                            }}
                                        >
                                            {Array.isArray(metadataError) ? (
                                                metadataError.map((err, index) => (
                                                    <li key={index}>
                                                        <Typography variant="body1">{err}</Typography>
                                                    </li>
                                                ))
                                            ) : (
                                                <li>
                                                    <Typography variant="body1">
                                                        {metadataError}
                                                    </Typography>
                                                </li>
                                            )}
                                        </ul>
                                    </Box>
                                )}
                            </Collapse>

                            <Collapse in={submitAttempted && !metadataFile && !metadataError}>
                                {submitAttempted && !metadataFile && !metadataError && (
                                    <Typography
                                        sx={{ color: "#ff4d4f", fontSize: "14px", mt: 1, ml: 1 }}
                                    >
                                        Please upload a metadata file.
                                    </Typography>
                                )}
                            </Collapse>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.25)" }} />

            <Typography variant="h4" sx={{ mb: 3, fontWeight: 500 }}>
                Assembled genome
            </Typography>

            <Grid container sx={{ width: "100%" }} spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <Box sx={{ width: "100%" }}>
                        <Typography sx={{ opacity: 0.9, mb: 1 }} variant="body1">
                            The name should follow the nomenclature:
                        </Typography>

                        <Box component="ul" sx={{ m: 0, pl: 3, opacity: 0.9, mb: 2 }}>
                            <Typography variant="body2">
                                <li>sampleID.fasta</li>
                                <li>sampleID.fa</li>
                                <li>sampleID.fna</li>
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                mt: 3,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Select
                                fullWidth
                                displayEmpty
                                IconComponent={ExpandMoreIcon}
                                value={selectedGenome}
                                onChange={(e) => {
                                    setSelectedGenome(e.target.value);
                                    removeError("Select a reference genome");
                                }}
                                sx={{
                                    borderRadius: 0,
                                    color: "#fff",
                                    ".MuiOutlinedInput-notchedOutline": {
                                        border: "none",
                                        borderTop: genomeHasError
                                            ? "1px solid #ff4d4f"
                                            : "1px solid #fff",
                                        borderBottom: genomeHasError
                                            ? "1px solid #ff4d4f"
                                            : "1px solid #fff",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderTop: genomeHasError
                                            ? "1px solid #ff4d4f"
                                            : "1px solid #fff",
                                        borderBottom: genomeHasError
                                            ? "1px solid #ff4d4f"
                                            : "1px solid #fff",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderTop: genomeHasError
                                            ? "1px solid #ff4d4f"
                                            : "1px solid #fff",
                                        borderBottom: genomeHasError
                                            ? "1px solid #ff4d4f"
                                            : "1px solid #fff",
                                    },
                                    ".MuiSelect-icon": {
                                        color: genomeHasError ? "#ff4d4f" : "#fff",
                                        fontSize: 28,
                                        right: 8,
                                    },
                                    width: "100%",
                                    maxWidth: "520px",
                                    height: "59px",
                                }}
                                renderValue={(v) => (v ? v : "Select a reference genome")}
                            >
                                {genomes.map((g) => (
                                    <MenuItem key={g} value={g}>
                                        {g}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        <Collapse in={genomeHasError}>
                            {genomeHasError && (
                                <Typography
                                    sx={{ color: "#ff4d4f", fontSize: "14px", mt: 1, ml: 1 }}
                                >
                                    Please select a reference genome.
                                </Typography>
                            )}
                        </Collapse>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, width: "100%" }}>
                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Button
                                    variant="contained"
                                    component="label"
                                    sx={{
                                        border: fastaHasError
                                            ? "1px solid #ff4d4f"
                                            : "1px solid transparent",
                                        color: "#000",
                                        borderRadius: 9999,
                                        px: 3,
                                        py: 1.25,
                                        width: "100%",
                                        maxWidth: "415px",
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        "&:hover": {
                                            bgcolor: "#111",
                                            boxShadow: "none",
                                            color: "#fff",
                                            border: "1px solid #fff",
                                        },
                                    }}
                                    color="iconoBlanco"
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        Upload your FASTA file
                                    </Typography>

                                    <FileUploadIcon sx={{ width: "30px", height: "30px" }} />

                                    <input
                                        type="file"
                                        hidden
                                        onChange={handleFileSelect}
                                    />
                                </Button>

                                {selectedFile && !fastaError && showGreenCheck(true)}
                            </Box>

                            <Collapse in={Boolean(fastaError) || (submitAttempted && !selectedFile)}>
                                {(Boolean(fastaError) || (submitAttempted && !selectedFile)) && (
                                    <Typography
                                        sx={{
                                            color: "#ff4d4f",
                                            fontSize: "14px",
                                            mt: 1,
                                            ml: 1,
                                            whiteSpace: "pre-line",
                                        }}
                                    >
                                        {fastaError || "Please upload a FASTA file."}
                                    </Typography>
                                )}
                            </Collapse>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.25)" }} />

            <Typography variant="h4" sx={{ mb: 3, fontWeight: 500 }}>
                Contact
            </Typography>

            <Grid container sx={{ width: "100%" }} spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <Box sx={{ width: "100%" }}>
                        <Typography variant="body1" sx={{ width: "80%", mt: 3 }}>
                            Personal data collected through this medium will not be stored in any
                            database nor will it be subject to further processing. Your
                            information will be used exclusively to send a notification when the
                            processing is over to the email address provided.
                        </Typography>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <Typography variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                        Provide the email where the results will be sent.
                    </Typography>
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            mt: 3,
                        }}
                    >
                        <InputBase
                            placeholder="Email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                removeError("Enter a valid email");
                            }}
                            sx={{
                                border: emailHasError ? "1px solid #ff4d4f" : "1px solid #fff",
                                bgcolor: "#000",
                                color: "#fff",
                                borderRadius: 9999,
                                px: 3,
                                py: 1.25,
                                width: "100%",
                                maxWidth: "415px",
                            }}
                        />
                        {isValidEmail(email) && showGreenCheck(true)}
                    </Box>

                    <Collapse in={emailHasError}>
                        {emailHasError && (
                            <Typography
                                sx={{ color: "#ff4d4f", fontSize: "14px", mt: 1, ml: 1 }}
                            >
                                Please enter a valid email.
                            </Typography>
                        )}
                    </Collapse>
                </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.25)" }} />

            <Grid container sx={{ width: "100%" }} spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={acceptedTos}
                                onChange={(e) => {
                                    setAcceptedTos(e.target.checked);
                                    removeError("Accept Terms of Service");
                                }}
                                sx={{
                                    border: tosHasError
                                        ? "2px solid #ff4d4f"
                                        : "2px solid #fff",
                                    borderRadius: "4px",
                                    width: "15px",
                                    height: "15px",
                                    mt: 3,
                                }}
                                icon={
                                    <Box
                                        sx={{
                                            width: "15px",
                                            height: "15px",
                                            border: "2px solid transparent",
                                            borderRadius: "4px",
                                        }}
                                    />
                                }
                                checkedIcon={
                                    <Box
                                        sx={{
                                            width: "15px",
                                            height: "15px",
                                            backgroundColor: "#000",
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                background: "#20BC71",
                                                width: "12px",
                                                height: "12px",
                                                borderRadius: "2px",
                                            }}
                                        />
                                    </Box>
                                }
                            />
                        }
                        label={
                            <Typography variant="body1" sx={{ width: "80%", mt: 3 }}>
                                I accept that the Latin America Resistance Atlas collects
                                pathogen identification, antimicrobial resistance gene list, date,
                                geographical region and the genomic sequence. The summary of
                                the data collected will be added to the statistics shown in the
                                Browse section.
                            </Typography>
                        }
                        sx={{ alignItems: "flex-start", gap: 2, mb: 2 }}
                    />

                    <Collapse in={tosHasError}>
                        {tosHasError && (
                            <Typography
                                sx={{ color: "#ff4d4f", fontSize: "14px", mt: 1, ml: 1 }}
                            >
                                Please accept the Terms of Service.
                            </Typography>
                        )}
                    </Collapse>
                </Grid>

                <Grid
                    size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    }}
                >
                    <UploadedThreeFilesDisplay
                        metaData={metadataFile}
                        r1File={selectedFile}
                        r2File={null}
                        labels={["Metadata", "FASTA", ""]}
                        onRemoveVariable1={() => {
                            setMetadataFile(null);
                            setMetadataError(null);
                        }}
                        onRemoveR1={() => {
                            setSelectedFile(null);
                            setFastaError(null);
                        }}
                        onRemoveR2={() => { }}
                        inactive={true}
                    />

                    <Button
                        variant="contained"
                        disabled={openLoadModal}
                        sx={{
                            width: "100%",
                            maxWidth: "270px",
                            borderRadius: "9999px",
                            color: "black",
                            height: "56px",
                            mt: 2,
                            "&:hover": { bgcolor: "#fff", boxShadow: "none", color: "#000" },
                            "&.Mui-disabled": {
                                opacity: 0.6,
                                color: "#000",
                            },
                        }}
                        color="green"
                        onClick={(e) => {
                            handleSubmit(e);
                        }}
                    >
                        <Typography variant="body1">ANALYZE</Typography>
                    </Button>

                    <Collapse in={submitAttempted && formErrors.length > 0}>
                        {submitAttempted && formErrors.length > 0 && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#ff4d4f",
                                    mt: 1.5,
                                    maxWidth: "415px",
                                }}
                            >
                                Please review the form. Some required fields are missing or invalid.
                            </Typography>
                        )}
                    </Collapse>
                </Grid>
            </Grid>

            <ProcessingModal
                open={openLoadModal}
                onClose={() => {
                    setOpenLoadModal(false);
                }}
                filesProgress={uploadProgress}
            />

            <UploadCompleteModal
                open={openUploadCompleteModal}
                onClose={() => {
                    setOpenUploadCompleteModal(false);
                }}
            />

            <ErrorModal
                open={openErrorModal}
                onClose={() => {
                    setOpenErrorModal(false);
                }}
                title={errorTitle}
                message={errorMessage}
            />
        </Box>
    );
}